/**
 * CostChip.jsx
 *
 * Tiny money pill used in places where we want a compact cost readout —
 * case list rows, history event rows, etc.
 *
 * Props:
 *   costs    — { GBP: 1234.50, EUR: 320.00 } shape. null/empty → renders nothing.
 *   amount   — alternative: single amount + currency for one-off use
 *   currency
 *   size     — 'sm' (default) | 'md'
 */

import React from 'react';

/**
 * CostCategoryPill — labels a cost line-item's category.
 * Grey fill + coloured border/text, NO dot — distinct from status badges so the
 * two systems read differently. cssClass is a cost-* class from config.
 */
export function CostCategoryPill({ cssClass, label }) {
  return <span className={`cost-pill ${cssClass || 'cost-other'}`}>{label}</span>;
}

function formatMoney(amount, currency) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(Number(amount) || 0);
  } catch {
    return `${currency} ${Math.round(Number(amount) || 0).toLocaleString()}`;
  }
}

export default function CostChip({ costs, amount, currency, size = 'sm' }) {
  // Single-amount mode (used by history rows)
  if (amount != null && currency) {
    return (
      <span className={`cost-chip cost-chip-${size}`}>
        {formatMoney(amount, currency)}
      </span>
    );
  }

  // Multi-currency mode (used by case list rows)
  if (!costs || typeof costs !== 'object') return null;
  const entries = Object.entries(costs).filter(([, v]) => Number(v) > 0);
  if (!entries.length) return null;

  return (
    <span className={`cost-chip cost-chip-${size}`}>
      {entries.map(([cur, total], i) => (
        <React.Fragment key={cur}>
          {i > 0 && <span className="cost-chip-sep">·</span>}
          {formatMoney(total, cur)}
        </React.Fragment>
      ))}
    </span>
  );
}
